import React from "react"
import PropTypes from "prop-types"
import styles from "./MenuItem.module.css"
import { useDispatch, useSelector } from "react-redux"
import { metadataSelector } from "store/selectors"
import classNames from "classnames"
import { useNavigate, useLocation } from "react-router-dom"
import { Icon } from "ui/Icon"
import { setProHomeMenuClose, setProHomeMenuItemActive } from "store/pro-home-menu"
import { DownOutlined } from "@ant-design/icons"

const SubMenuItem = ({ item }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const isActive = item.isActive ? item.isActive(location.pathname) : location.pathname === item.path

  const handleClick = () => {
    dispatch(setProHomeMenuClose())
    navigate(item.path)
  }

  return (
    <button
      type="button"
      className={classNames(styles.subMenuItem, isActive && styles.subMenuItemActive)}
      onClick={handleClick}
    >
      {item.name}
    </button>
  )
}

SubMenuItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    isActive: PropTypes.func
  }).isRequired
}

export const MenuItem = ({ item }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const metaData = useSelector(metadataSelector("conversations"))
  const { guide } = useSelector((state) => state.user)
  const { activeMenu } = useSelector((state) => state.proHomeMenu)
  const { name, id, iconName, iconType, path, openInNewTab, children } = item
  const isActive = activeMenu === id
  const hasUnviewedMessages = metaData.total_unviewed_messages > 0 && id === "messages"

  const onClickHandler = () => {
    // A parent item with children: if its submenu is already open, clicking collapses it.
    if (children?.length > 0 && isActive) {
      dispatch(setProHomeMenuItemActive(null))
      return
    }
    // Otherwise open its submenu immediately, then fall through to navigate to its page.
    if (children?.length > 0) {
      dispatch(setProHomeMenuItemActive(id))
    }
    dispatch(setProHomeMenuClose())
    let dynamicPath = typeof path === "string" ? path : path({ slug: guide.slug })
    if (id === "calendar") {
      const [base, query] = dynamicPath.split('?');
      const searchParams = new URLSearchParams(query || '');
      if (!searchParams.get('tab')) {
        searchParams.set('tab', 'all-bookings');
      }
      dynamicPath = `${base}?${searchParams.toString()}`;
    }
    if (openInNewTab) {
      const isFullUrl = dynamicPath.startsWith("http://") || dynamicPath.startsWith("https://")
      let url
      if (isFullUrl) {
        url = dynamicPath
      } else {
        const normalizedPath = dynamicPath.startsWith("/") ? dynamicPath : `/${dynamicPath}`
        url = `${window.location.origin}${window.location.pathname}#${normalizedPath}`
      }
      window.open(url, "_blank", "noopener")
    } else {
      navigate(dynamicPath)
    }
  }

  return (
    <>
      <button type="button" className={classNames(styles.menuGroupItem, isActive && styles.menuGroupItemActive)} onClick={onClickHandler}>
        <Icon name={iconName} type={iconType} strokeWidth={1} className={styles.itemIcon} color="currentColor" />
        <div className={styles.itemName}>{name}</div>
        {hasUnviewedMessages && <div className={styles.unviewed}>{metaData.total_unviewed_messages}</div>}
        {children?.length > 0 && (
          <DownOutlined className={classNames(styles.chevron, isActive && styles.chevronOpen)} />
        )}
      </button>
      {isActive && children?.length > 0 && (
        <div className={styles.subMenuList}>
          {children.map((child) => (
            <SubMenuItem key={child.id} item={child} />
          ))}
        </div>
      )}
    </>
  )
}

MenuItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    iconName: PropTypes.string,
    iconType: PropTypes.string,
    path: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
    openInNewTab: PropTypes.bool,
    children: PropTypes.array
  }).isRequired
}
